'use client';

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import { useAppStore } from '@/store/app';
import { api } from '@/lib/api';
import { formatPrice, formatPercent, formatTime } from '@/lib/format';
import ResearchTabs from '@/components/ResearchTabs';
import { buildCoinRoute, parseInstrumentId } from '@/lib/instrument';

/* ------------------------------ types ------------------------------ */

interface ModuleCfg {
  key: string;
  visible: boolean;
  order: number;
}

interface WatchRow {
  symbol: string;
  baseAsset?: string;
  price: number;
  changePct: number;
  quoteVolume?: number;
  exchange?: string;
}
interface HitRow { id: string; title: string; body: string; symbol: string | null; refUrl: string | null; createdAt: string; snapshot?: Record<string, unknown> | null; }
interface EventRow { eventKey: string; title: string; eventType: string; eventDate: string; status: string; leadHours: number; }
interface AlertRow { id: string; symbol: string; condition: string; targetValue: number; status: string; triggeredAt: string | null; createdAt: string; }
interface RuleRow { id: string; name: string; description: string; updatedAt: string; }
interface LayoutRow { id: string; name: string; instrumentId: string; range: string; updatedAt: string; }
interface NoteRow { id: string; targetType: string; targetId: string | null; targetSnapshot: Record<string, unknown> | null; available: boolean; body: string; createdAt: string; updatedAt: string; }

interface ScreenMonitor {
  state: 'no_subscription' | 'paused' | 'failed' | 'delayed' | 'monitoring';
  count: number;
  activeCount: number;
  pausedCount: number;
  failedCount: number;
  lastEvaluatedAt: string | null;
  lastError: string | null;
  stale: boolean;
}

/* N03: 观察计划 */
interface InstrumentRef { symbol: string; exchange?: string; product?: string }
interface EvidenceRef { kind: string; id?: string; text?: string }
interface RuleRef { type: 'subscription'; id: string }
interface PlanRow {
  id: string;
  title: string;
  instrumentRefs: InstrumentRef[];
  hypothesis: string;
  evidenceRefs: EvidenceRef[];
  entryRuleRef: RuleRef | null;
  invalidationRuleRef: RuleRef | null;
  reviewAt: string | null;
  status: 'draft' | 'watching' | 'triggered' | 'invalidated' | 'reviewed' | 'archived';
  overdue: boolean;
  notes: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}
interface SubRow { id: string; screenName?: string; state?: string; screenId?: string }
interface PlanPayload {
  id?: string;
  title: string;
  instrumentRefs: InstrumentRef[];
  hypothesis: string;
  evidenceRefs: EvidenceRef[];
  entryRuleRef: RuleRef | null;
  invalidationRuleRef: RuleRef | null;
  reviewAt: string | null;
  notes: string | null;
}

interface Overview {
  asOf: string;
  watchlistChanges: WatchRow[];
  screenHits: HitRow[];
  screenMonitor: ScreenMonitor;
  events: EventRow[];
  alerts: AlertRow[];
  savedResearch: { rules: RuleRow[]; layouts: LayoutRow[]; subscriptions: number; favorites: number; notes: number };
  notes: NoteRow[];
  observationPlans: { dueForReview: PlanRow[]; triggered: PlanRow[]; invalidated: PlanRow[]; drafts: PlanRow[]; total: number };
  counts: { watchlist: number; rules: number; layouts: number; subscriptions: number; favorites: number; alerts: number; notes: number };
}

const MODULE_KEYS = ['watchlist_changes', 'screen_hits', 'events', 'alerts', 'saved_research', 'notes', 'observation_plans'] as const;
type ModuleKey = (typeof MODULE_KEYS)[number];

const NOTE_TARGETS = ['general', 'signal', 'layout', 'rule', 'subscription', 'study', 'coin'] as const;

/** D20: 预警条件内部枚举 → 中文标签（与 /alerts 页字典一致，不裸漏枚举）。 */
const ALERT_CONDITION_LABELS: Record<string, { zh: string; en: string; percent?: boolean }> = {
  price_above: { zh: '价格高于', en: 'Above' },
  price_below: { zh: '价格低于', en: 'Below' },
  change_percent_above: { zh: '24h 涨幅超过', en: '24h change above', percent: true },
  change_percent_below: { zh: '24h 跌幅超过', en: '24h change below', percent: true },
  volume_spike: { zh: '成交量激增', en: 'Volume spike' },
  cross_above: { zh: '上穿', en: 'Crossed above' },
  cross_below: { zh: '下穿', en: 'Crossed below' },
  funding_above: { zh: '费率高于', en: 'Funding above', percent: true },
  funding_below: { zh: '费率低于', en: 'Funding below', percent: true },
  oi_above: { zh: '持仓量高于', en: 'OI above' },
  oi_below: { zh: '持仓量低于', en: 'OI below' },
  rsi_above: { zh: 'RSI 高于', en: 'RSI above', percent: true },
  rsi_below: { zh: 'RSI 低于', en: 'RSI below', percent: true },
};

/** D20: 预警条件 + 目标值（RSI/费率/涨跌幅类不带 $，价格类带 $）。 */
function fmtAlertCondition(a: AlertRow, zh: boolean): string {
  const meta = ALERT_CONDITION_LABELS[a.condition] ?? { zh: a.condition, en: a.condition };
  const label = zh ? meta.zh : meta.en;
  const v = meta.percent ? `${a.targetValue}%` : formatPrice(a.targetValue);
  return `${label} ${v}`;
}

/* ------------------------------ helpers ------------------------------ */

function fmtDate(ts: string | Date): string {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** D09: 条件命中空态文案 — 区分「未创建订阅 / 已暂停 / 监控中无命中 / 评估延迟 / 评估失败」。 */
function screenEmptyMsg(sm: ScreenMonitor, zh: boolean): string {
  if (sm.state === 'no_subscription' || sm.count === 0) return zh ? '尚未创建条件订阅' : 'No condition subscription yet';
  if (sm.state === 'paused') return zh ? `订阅已暂停（${sm.count} 个）` : `Subscriptions paused (${sm.count})`;
  if (sm.state === 'failed') return zh ? `评估失败（${sm.failedCount} 个）` : `Evaluation failed (${sm.failedCount})`;
  if (sm.state === 'delayed') return zh ? '评估延迟 — 数据可能不新鲜' : 'Evaluation delayed — data may be stale';
  return zh ? '监控中，暂无命中' : 'Monitoring, no hits yet';
}

/* ------------------------------ page ------------------------------ */

export default function WorkspacePage() {
  const router = useRouter();
  const qc = useQueryClient();
  const { language, user, authState } = useAppStore();
  const zh = language !== 'en';
  const authed = authState === 'authenticated' && !!user;

  const t = useMemo(
    () => ({
      title: zh ? '我的研究工作台' : 'My Research Workspace',
      subtitle: zh ? '聚合自选变化、条件命中、事件、预警与已保存研究' : 'Watchlist moves, screen hits, events, alerts and saved research',
      customize: zh ? '自订模组' : 'Customize modules',
      overview: zh ? '研究概览' : 'Overview',
      needLogin: zh ? '请先登入以查看你的研究工作台' : 'Sign in to view your research workspace',
      login: zh ? '前往登入' : 'Go to login',
      loading: zh ? '载入中…' : 'Loading…',
      retry: zh ? '重试' : 'Retry',
      failed: zh ? '载入失败' : 'Failed to load',
      viewAll: zh ? '查看全部' : 'View all',
      empty: zh ? '暂无数据' : 'Nothing yet',
      save: zh ? '储存' : 'Save',
      cancel: zh ? '取消' : 'Cancel',
      edit: zh ? '编辑' : 'Edit',
      del: zh ? '删除' : 'Delete',
      newNote: zh ? '新建笔记' : 'New note',
      saved: zh ? '已储存' : 'Saved',
      fail: zh ? '操作失败，请重试' : 'Failed, please retry',
      confirmDel: zh ? '确定删除这条笔记？' : 'Delete this note?',
      module: {
        watchlist_changes: { name: zh ? '自选变化' : 'Watchlist Moves', icon: '⭐', empty: zh ? '尚未添加自选，前往添加' : 'No watchlist items yet' },
        screen_hits: { name: zh ? '条件命中' : 'Screen Hits', icon: '🎯', empty: zh ? '暂无条件命中' : 'No screen hits yet' },
        events: { name: zh ? '事件' : 'Events', icon: '📅', empty: zh ? '尚未订阅事件提醒' : 'No event reminders yet' },
        alerts: { name: zh ? '预警' : 'Alerts', icon: '⏰', empty: zh ? '暂无价格预警' : 'No price alerts yet' },
        saved_research: { name: zh ? '已保存研究' : 'Saved Research', icon: '🗂', empty: zh ? '暂无已保存研究' : 'No saved research yet' },
        notes: { name: zh ? '研究笔记' : 'Research Notes', icon: '📝', empty: zh ? '暂无研究笔记' : 'No research notes yet' },
        observation_plans: { name: zh ? '观察计划' : 'Observation Plans', icon: '🔭', empty: zh ? '暂无观察计划' : 'No observation plans yet' },
      },
      plan: {
        new: zh ? '新建观察计划' : 'New plan',
        title: zh ? '标题' : 'Title',
        titlePh: zh ? '例如：BTC 突破后回踩确认' : 'e.g. BTC breakout retest',
        instruments: zh ? '观察币对' : 'Instruments',
        instrumentsPh: zh ? '输入币种关键词搜寻并选择' : 'Search and pick symbols',
        addInstrument: zh ? '添加币对' : 'Add pair',
        hypothesis: zh ? '研究假设' : 'Hypothesis',
        hypothesisPh: zh ? '写下你的研究假设与判断依据' : 'State your hypothesis and reasoning',
        evidence: zh ? '支持证据（每行一条）' : 'Supporting evidence (one per line)',
        evidencePh: zh ? '可引用信号/研究/规则编号或一句话理由' : 'Signal/study/rule id or a one-line reason',
        entryRule: zh ? '触发条件（条件订阅）' : 'Entry condition (subscription)',
        invalidationRule: zh ? '失效条件（条件订阅，可选）' : 'Invalidation condition (optional)',
        noRule: zh ? '未选择（条件不完整则保持草稿）' : 'None (plan stays draft while incomplete)',
        reviewAt: zh ? '覆核日期' : 'Review date',
        reviewHint: zh ? '到期的观察计划会出现在「待覆核」' : 'Plans due for review appear under "due for review"',
        notes: zh ? '备注' : 'Notes',
        notesPh: zh ? '补充记录（可选）' : 'Optional notes',
        save: zh ? '储存计划' : 'Save plan',
        status: zh ? '状态' : 'Status',
        version: zh ? 'v' : 'v',
        due: zh ? '待覆核' : 'Due for review',
        overdue: zh ? '已过期' : 'Overdue',
        draft: zh ? '草稿' : 'Draft',
        triggered: zh ? '已触发' : 'Triggered',
        invalidated: zh ? '已失效' : 'Invalidated',
        reviewed: zh ? '已覆核' : 'Reviewed',
        archived: zh ? '已归档' : 'Archived',
        watch: zh ? '开始观察' : 'Start watching',
        markTriggered: zh ? '标记已触发' : 'Mark triggered',
        markInvalidated: zh ? '标记已失效' : 'Mark invalidated',
        markReviewed: zh ? '标记已覆核' : 'Mark reviewed',
        rewatch: zh ? '重新观察' : 'Rewatch',
        archive: zh ? '归档' : 'Archive',
        incomplete: zh ? '需先填写触发条件与覆核日期，才能开始观察' : 'Add an entry condition and a review date before watching',
        ruleChangeConfirm: zh ? '修改进行中计划的条件会建立新版本（规则升级需确认），是否继续？' : 'Changing conditions of an active plan creates a new version — continue?',
        open: zh ? '查看' : 'View',
        created: zh ? '创建于' : 'Created',
        none: zh ? '暂无计划' : 'No plans yet',
        goScreener: zh ? '先到筛选器建立条件订阅 →' : 'Create a condition subscription in the screener →',
        ruleDeleted: zh ? '条件订阅已删除' : 'Subscription deleted',
        confirmDel: zh ? '确定删除这条观察计划？' : 'Delete this observation plan?',
      },
      notes: {
        placeholder: zh ? '输入笔记内容（最多 5000 字）' : 'Write your note (max 5000 chars)',
        targetType: zh ? '关联类型' : 'Target type',
        targetSearch: zh ? '搜寻并选择对象（输入币种/名称/日期）' : 'Search and select a target',
        targetPick: zh ? '选择对象' : 'Pick target',
        targetPicked: zh ? '已关联' : 'Linked',
        searchHint: zh ? '输入 ≥1 个字元搜寻' : 'Type ≥1 char to search',
        noResult: zh ? '无匹配对象' : 'No matches',
        unavailable: zh ? '原对象不可用' : 'Target unavailable',
        clear: zh ? '清除' : 'Clear',
        general: zh ? '通用' : 'General',
        signal: zh ? '信号' : 'Signal',
        layout: zh ? '图表布局' : 'Chart layout',
        rule: zh ? '筛选规则' : 'Screener rule',
        subscription: zh ? '条件订阅' : 'Subscription',
        study: zh ? '历史研究' : 'Historical study',
        coin: zh ? '币对' : 'Coin pair',
        count: (n: number) => `${n}/5000`,
      },
      fromSignal: zh ? '从信号添加笔记' : 'Add a note from a signal',
      restoreDefault: zh ? '恢复默认' : 'Restore default',
      stat: {
        watchlist: zh ? '自选' : 'Watchlist',
        rules: zh ? '规则' : 'Rules',
        layouts: zh ? '布局' : 'Layouts',
        subscriptions: zh ? '订阅' : 'Subscriptions',
        favorites: zh ? '信号收藏' : 'Favorites',
        notes: zh ? '笔记' : 'Notes',
      },
      setting: {
        title: zh ? '自订模组' : 'Customize modules',
        visible: zh ? '显示' : 'Visible',
        hint: zh ? '拖动调整顺序，或隐藏不需要的模组' : 'Reorder modules, or hide the ones you do not need',
      },
      alertsCol: zh ? '预警' : 'Alerts',
      hitCol: zh ? '命中' : 'Hits',
      ruleCol: zh ? '规则' : 'Rules',
      layoutCol: zh ? '布局' : 'Layouts',
      price: zh ? '价格' : 'Price',
      change: zh ? '24h' : '24h',
      time: zh ? '时间' : 'Time',
      target: zh ? '条件' : 'Condition',
      status: zh ? '状态' : 'Status',
    }),
    [zh],
  );

  /* ------------------------- data fetching ------------------------- */

  const overviewQ = useQuery({
    queryKey: ['workspace-overview'],
    queryFn: () => api.get<Overview>('/workspace/overview'),
    enabled: authed,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const layoutQ = useQuery({
    queryKey: ['workspace-layout'],
    queryFn: () => api.get<{ modules: ModuleCfg[] }>('/workspace/layout'),
    enabled: authed,
  });

  /* N03: 条件订阅列表（观察计划触发/失效条件选择器） */
  const subsQ = useQuery({
    queryKey: ['plans-subs'],
    queryFn: () => api.get<SubRow[]>('/subscriptions'),
    enabled: authed,
    staleTime: 30_000,
  });

  const [modules, setModules] = useState<ModuleCfg[]>([]);
  useEffect(() => {
    if (layoutQ.data?.modules?.length) {
      setModules(layoutQ.data.modules);
    } else if (authed && !layoutQ.isLoading) {
      setModules(MODULE_KEYS.map((key, order) => ({ key, visible: true, order })));
    }
  }, [layoutQ.data, layoutQ.isLoading, authed]);

  const visibleModules = useMemo(
    () => [...modules].filter((m) => m.visible).sort((a, b) => a.order - b.order),
    [modules],
  );

  /* --------------------------- mutations --------------------------- */

  const saveLayout = useMutation({
    mutationFn: (list: ModuleCfg[]) => api.put('/workspace/layout', { modules: list }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workspace-layout'] });
      setSettingsOpen(false);
    },
  });

  const saveNote = useMutation({
    mutationFn: (payload: { id?: string; targetType: string; targetId?: string; content: string; snapshot?: Record<string, unknown> | null }) =>
      payload.id
        ? api.patch(`/workspace/notes/${payload.id}`, { content: payload.content })
        : api.post('/workspace/notes', {
            targetType: payload.targetType,
            targetId: payload.targetId || undefined,
            snapshot: payload.snapshot ?? null,
            content: payload.content,
          }),
    onSuccess: () => {
      setNoteModal(null);
      setNoteBody('');
      setNoteTarget('general');
      setNotePick(null);
      qc.invalidateQueries({ queryKey: ['workspace-overview'] });
    },
  });

  const delNote = useMutation({
    mutationFn: (id: string) => api.delete(`/workspace/notes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace-overview'] }),
  });

  /* --------------------------- N03 plan mutations --------------------------- */

  const savePlan = useMutation({
    mutationFn: (payload: {
      id?: string;
      title: string;
      instrumentRefs: InstrumentRef[];
      hypothesis: string;
      evidenceRefs: EvidenceRef[];
      entryRuleRef: RuleRef | null;
      invalidationRuleRef: RuleRef | null;
      reviewAt: string | null;
      notes: string | null;
      confirmVersion?: boolean;
    }) =>
      payload.id
        ? api.patch(`/research-plans/${payload.id}`, payload)
        : api.post('/research-plans', payload),
    onSuccess: () => {
      setPlanModal(null);
      qc.invalidateQueries({ queryKey: ['workspace-overview'] });
    },
  });

  const planStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.patch(`/research-plans/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace-overview'] }),
  });

  const delPlan = useMutation({
    mutationFn: (id: string) => api.delete(`/research-plans/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace-overview'] }),
  });

  /* --------------------------- local state --------------------------- */

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [noteModal, setNoteModal] = useState<null | { mode: 'create' } | { mode: 'edit'; note: NoteRow }>(null);
  const [noteBody, setNoteBody] = useState('');
  const [noteTarget, setNoteTarget] = useState<(typeof NOTE_TARGETS)[number]>('general');
  const [notePick, setNotePick] = useState<{ value: string; name: string } | null>(null);
  const [noteQ, setNoteQ] = useState('');
  const [toast, setToast] = useState('');

  /* --------------------------- N03 plan form state --------------------------- */

  const [planModal, setPlanModal] = useState<null | { mode: 'create' } | { mode: 'edit'; plan: PlanRow }>(null);
  const [planTitle, setPlanTitle] = useState('');
  const [planInsts, setPlanInsts] = useState<InstrumentRef[]>([]);
  const [planHypo, setPlanHypo] = useState('');
  const [planEvid, setPlanEvid] = useState('');
  const [planEntry, setPlanEntry] = useState<string>('');
  const [planInvalid, setPlanInvalid] = useState<string>('');
  const [planReview, setPlanReview] = useState('');
  const [planNotes, setPlanNotes] = useState('');
  const [planInstQ, setPlanInstQ] = useState('');
  const [planInstPick, setPlanInstPick] = useState<InstrumentRef | null>(null);
  const [planConfirmVersion, setPlanConfirmVersion] = useState(false);
  const [ruleChangePending, setRuleChangePending] = useState<PlanPayload | null>(null);

  /* N03: 币对详情「加入观察计划」入口预填：/workspace?new=SYMBOL */
  useEffect(() => {
    const sym = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('new') : null;
    if (!sym) return;
    const clean = sym.trim().toUpperCase();
    if (!clean || planModal) return;
    setPlanModal({ mode: 'create' });
    setPlanTitle('');
    setPlanInsts([{ symbol: clean }]);
    setPlanHypo('');
    setPlanEvid('');
    setPlanEntry('');
    setPlanInvalid('');
    setPlanReview('');
    setPlanNotes('');
    setPlanInstQ('');
    setPlanInstPick(null);
    setPlanConfirmVersion(false);
    setRuleChangePending(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('new');
    window.history.replaceState({}, '', url.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** D20: 对象选择器搜索（类型→关键词→候选）。 */
  const targetQ = useQuery({
    queryKey: ['note-targets', noteTarget, noteQ],
    queryFn: () =>
      api.get<{ value: string; name: string; sub: string }[]>(
        `/workspace/note-targets?type=${noteTarget}&q=${encodeURIComponent(noteQ)}`,
      ),
    enabled: noteModal !== null && noteTarget !== 'general' && noteQ.trim().length >= 1,
    staleTime: 15_000,
  });

  /* N03: 币对选择器（复用 note-targets?type=coin） */
  const planCoinQ = useQuery({
    queryKey: ['plan-coins', planInstQ],
    queryFn: () =>
      api.get<{ value: string; name: string; sub: string }[]>(
        `/workspace/note-targets?type=coin&q=${encodeURIComponent(planInstQ)}`,
      ),
    enabled: planModal !== null && planInstQ.trim().length >= 1,
    staleTime: 15_000,
  });

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  /* --------------------------- render helpers --------------------------- */

  const openNote = (mode: 'create' | 'edit', note?: NoteRow) => {
    if (mode === 'create') {
      setNoteModal({ mode: 'create' });
      setNoteBody('');
      setNoteTarget('general');
      setNotePick(null);
      setNoteQ('');
    } else if (note) {
      setNoteModal({ mode: 'edit', note });
      setNoteBody(note.body);
      const tt = (NOTE_TARGETS as readonly string[]).includes(note.targetType) ? (note.targetType as (typeof NOTE_TARGETS)[number]) : 'general';
      setNoteTarget(tt);
      setNotePick(
        note.targetSnapshot?.name
          ? { value: note.targetId ?? '', name: String(note.targetSnapshot.name) }
          : null,
      );
      setNoteQ('');
    }
  };

  const submitNote = () => {
    if (!noteModal) return;
    const content = noteBody.trim();
    if (!content) return;
    const snapshot =
      noteTarget !== 'general' && notePick
        ? { name: notePick.name, kind: noteTarget }
        : null;
    saveNote.mutate(
      {
        id: noteModal.mode === 'edit' ? noteModal.note.id : undefined,
        targetType: noteTarget,
        targetId: noteTarget !== 'general' ? notePick?.value : undefined,
        snapshot,
        content,
      },
      { onError: () => showToast(t.fail) },
    );
  };

  /* --------------------------- N03 plan helpers --------------------------- */

  const openPlan = (mode: 'create' | 'edit', plan?: PlanRow) => {
    if (mode === 'create') {
      setPlanModal({ mode: 'create' });
      setPlanTitle('');
      setPlanInsts([]);
      setPlanHypo('');
      setPlanEvid('');
      setPlanEntry('');
      setPlanInvalid('');
      setPlanReview('');
      setPlanNotes('');
      setPlanInstQ('');
      setPlanInstPick(null);
      setPlanConfirmVersion(false);
      setRuleChangePending(null);
    } else if (plan) {
      setPlanModal({ mode: 'edit', plan });
      setPlanTitle(plan.title);
      setPlanInsts(plan.instrumentRefs ?? []);
      setPlanHypo(plan.hypothesis ?? '');
      setPlanEvid((plan.evidenceRefs ?? []).map((e) => e.text ?? e.id ?? '').filter(Boolean).join('\n'));
      setPlanEntry(plan.entryRuleRef?.id ?? '');
      setPlanInvalid(plan.invalidationRuleRef?.id ?? '');
      setPlanReview(plan.reviewAt ? String(plan.reviewAt).slice(0, 10) : '');
      setPlanNotes(plan.notes ?? '');
      setPlanInstQ('');
      setPlanInstPick(null);
      setPlanConfirmVersion(false);
      setRuleChangePending(null);
    }
  };

  const planRuleChanged = (editing: PlanRow | undefined): boolean => {
    if (!editing) return false;
    const entryChanged = (editing.entryRuleRef?.id ?? '') !== planEntry;
    const invalidChanged = (editing.invalidationRuleRef?.id ?? '') !== planInvalid;
    return entryChanged || invalidChanged;
  };

  const submitPlan = () => {
    if (!planModal) return;
    const editing = planModal.mode === 'edit' ? planModal.plan : undefined;
    const payload = {
      id: editing?.id,
      title: planTitle.trim(),
      instrumentRefs: planInsts,
      hypothesis: planHypo.trim(),
      evidenceRefs: planEvid
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((text) => ({ kind: 'text' as const, text })),
      entryRuleRef: planEntry ? { type: 'subscription' as const, id: planEntry } : null,
      invalidationRuleRef: planInvalid ? { type: 'subscription' as const, id: planInvalid } : null,
      reviewAt: planReview || null,
      notes: planNotes.trim() || null,
    };
    const ruleChanged = planRuleChanged(editing);
    if (ruleChanged && !planConfirmVersion) {
      setRuleChangePending(payload);
      return;
    }
    savePlan.mutate(
      { ...payload, confirmVersion: ruleChanged && planConfirmVersion },
      { onError: () => showToast(t.fail) },
    );
  };

  const doPlanStatus = (id: string, status: string) => {
    planStatus.mutate({ id, status }, { onError: () => showToast(t.fail) });
  };

  const planStatusLabel = (s: string): string => {
    const map: Record<string, string> = {
      draft: t.plan.draft,
      watching: zh ? '观察中' : 'Watching',
      triggered: t.plan.triggered,
      invalidated: t.plan.invalidated,
      reviewed: t.plan.reviewed,
      archived: t.plan.archived,
    };
    return map[s] ?? s;
  };

  const planStatusColor = (s: string, overdue: boolean): string => {
    if (s === 'triggered') return 'bg-gf-positive/15 text-gf-positive';
    if (s === 'invalidated') return 'bg-gf-negative/15 text-gf-negative';
    if (s === 'watching' && overdue) return 'bg-gf-warn/15 text-gf-warn';
    if (s === 'watching') return 'bg-gf-accent/15 text-gf-accent';
    if (s === 'reviewed') return 'bg-gf-surface2 text-gf-muted';
    return 'bg-gf-surface2 text-gf-muted';
  };

  const renderPlanRow = (p: PlanRow) => {
    const symbols = (p.instrumentRefs ?? []).map((i) => i.symbol).join(' / ');
    return (
      <li key={p.id} className="py-2">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-medium text-gf-text">{p.title}</span>
              <span className={`rounded px-1.5 py-0.5 text-[10px] ${planStatusColor(p.status, p.overdue)}`}>
                {planStatusLabel(p.status)}
                {p.status === 'watching' && p.overdue && ` · ${t.plan.overdue}`}
              </span>
              <span className="rounded bg-gf-surface2 px-1 py-0.5 text-[10px] text-gf-muted">
                {t.plan.version}{p.version}
              </span>
            </div>
            <div className="mt-0.5 line-clamp-1 text-[11px] text-gf-muted">
              {symbols || t.plan.none}
            </div>
            {p.reviewAt && (p.status === 'watching' || p.status === 'draft') && (
              <div className="mt-0.5 text-[11px] text-gf-muted">
                {t.plan.reviewAt}: {String(p.reviewAt).slice(0, 10)}
              </div>
            )}
            {p.hypothesis && <div className="mt-1 line-clamp-2 text-xs text-gf-text/80">{p.hypothesis}</div>}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <div className="flex gap-1">
              {p.status === 'draft' && (
                <button onClick={() => openPlan('edit', p)} className="text-[11px] text-gf-accent hover:underline">
                  {t.edit}
                </button>
              )}
              {p.status === 'draft' && (
                <button
                  onClick={() => doPlanStatus(p.id, 'watching')}
                  className="text-[11px] text-gf-accent hover:underline"
                >
                  {t.plan.watch}
                </button>
              )}
              {p.status === 'watching' && (
                <>
                  <button onClick={() => doPlanStatus(p.id, 'reviewed')} className="text-[11px] text-gf-accent hover:underline">
                    {t.plan.markReviewed}
                  </button>
                  <button onClick={() => doPlanStatus(p.id, 'triggered')} className="text-[11px] text-gf-positive hover:underline">
                    {t.plan.markTriggered}
                  </button>
                  <button onClick={() => doPlanStatus(p.id, 'invalidated')} className="text-[11px] text-gf-negative hover:underline">
                    {t.plan.markInvalidated}
                  </button>
                </>
              )}
              {(p.status === 'triggered' || p.status === 'invalidated') && (
                <button onClick={() => doPlanStatus(p.id, 'watching')} className="text-[11px] text-gf-accent hover:underline">
                  {t.plan.rewatch}
                </button>
              )}
              <button
                onClick={() => doPlanStatus(p.id, 'archived')}
                className="text-[11px] text-gf-muted hover:text-gf-text"
                disabled={p.status === 'archived'}
              >
                {t.plan.archive}
              </button>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => {
                  if (confirm(t.plan.confirmDel)) delPlan.mutate(p.id, { onError: () => showToast(t.fail) });
                }}
                className="text-[11px] text-gf-negative hover:underline"
              >
                {t.del}
              </button>
            </div>
          </div>
        </div>
      </li>
    );
  };

  const renderPlanGroup = (title: string, rows: PlanRow[], emptyMsg: string) => (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[11px] font-medium text-gf-muted">{title}</span>
        {rows.length > 0 && <span className="text-[10px] text-gf-muted">{rows.length}</span>}
      </div>
      {rows.length ? (
        <ul className="divide-y divide-gf-border rounded-lg bg-gf-surface2 px-3">{rows.map(renderPlanRow)}</ul>
      ) : (
        <div className="rounded-lg bg-gf-surface2/60 px-3 py-2 text-[11px] text-gf-muted">{emptyMsg}</div>
      )}
    </div>
  );

  const renderModuleCard = (mk: ModuleKey, children: ReactNode, link?: string, badge?: ReactNode) => {
    const meta = t.module[mk];
    return (
      <section className="card flex flex-col p-4">
        <header className="mb-3 flex items-center gap-2">
          <span className="text-base">{meta.icon}</span>
          <h3 className="flex-1 text-sm font-semibold text-gf-text">{meta.name}</h3>
          {badge}
          {link && (
            <Link href={link} className="text-xs text-gf-accent hover:underline">
              {t.viewAll}
            </Link>
          )}
        </header>
        {children}
      </section>
    );
  };

  const emptyBlock = (msg: string, link?: string) => (
    <div className="flex items-center justify-between gap-2 rounded-lg bg-gf-surface2 px-3 py-2.5 text-xs text-gf-muted">
      <span>{msg}</span>
      {link && (
        <Link href={link} className="shrink-0 text-gf-accent hover:underline">
          →
        </Link>
      )}
    </div>
  );

  /* --------------------------- not authed --------------------------- */

  if (!authed) {
    return (
      <div className="mx-auto max-w-[1200px] px-3 py-4 sm:px-4">
        <ResearchTabs />
        <div className="card flex flex-col items-center gap-3 p-10 text-center">
          <div className="text-3xl">🔐</div>
          <div className="text-sm text-gf-muted">{t.needLogin}</div>
          <button
            onClick={() => router.push(`/login?redirect=${encodeURIComponent('/workspace')}`)}
            className="btn-primary !h-10 text-sm"
          >
            {t.login}
          </button>
        </div>
      </div>
    );
  }

  if (overviewQ.isLoading) {
    return (
      <div className="mx-auto max-w-[1200px] px-3 py-4 sm:px-4">
        <ResearchTabs />
        <div className="flex h-64 items-center justify-center text-sm text-gf-muted">{t.loading}</div>
      </div>
    );
  }

  if (overviewQ.isError) {
    return (
      <div className="mx-auto max-w-[1200px] px-3 py-4 sm:px-4">
        <ResearchTabs />
        <div className="card flex flex-col items-center gap-3 p-10 text-center">
          <div className="text-sm text-gf-muted">{t.failed}</div>
          <button onClick={() => overviewQ.refetch()} className="btn-outline !h-9 text-xs">
            {t.retry}
          </button>
        </div>
      </div>
    );
  }

  const ov = overviewQ.data!;
  const dataStale = Date.now() - (overviewQ.dataUpdatedAt ?? 0) > 120_000;

  /* --------------------------- module bodies --------------------------- */

  const bodyWatchlist = ov.watchlistChanges.length ? (
    <ul className="divide-y divide-gf-border">
      {ov.watchlistChanges.map((w) => (
        <li key={w.symbol} className="flex items-center gap-2 py-2 text-sm">
          <Link href={`/coin/${w.symbol}`} className="flex min-w-0 flex-1 items-center gap-2">
            <span className="font-medium text-gf-text">{w.symbol}</span>
            {w.exchange && <span className="truncate text-[11px] text-gf-muted">{w.exchange}</span>}
          </Link>
          <span className="font-mono text-gf-text">{formatPrice(w.price)}</span>
          <span className={`w-16 text-right font-mono ${w.changePct >= 0 ? 'text-gf-positive' : 'text-gf-negative'}`}>
            {formatPercent(w.changePct)}
          </span>
        </li>
      ))}
    </ul>
  ) : (
    emptyBlock(t.module.watchlist_changes.empty, '/watchlist')
  );

  const sm = ov.screenMonitor;
  const lastEvalTxt = sm.lastEvaluatedAt
    ? `${zh ? '上次评估' : 'Last eval'}: ${fmtDate(sm.lastEvaluatedAt)}`
    : zh ? '从未评估' : 'Never evaluated';

  const bodyObservationPlans = (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <button onClick={() => openPlan('create')} className="btn-outline !h-8 px-3 text-xs">
          + {t.plan.new}
        </button>
        <Link href="/screener" className="text-[11px] text-gf-accent hover:underline">
          {t.plan.goScreener}
        </Link>
      </div>
      <div className="space-y-3">
        {renderPlanGroup(t.plan.due, ov.observationPlans.dueForReview, t.plan.none)}
        {renderPlanGroup(t.plan.triggered, ov.observationPlans.triggered, t.plan.none)}
        {renderPlanGroup(t.plan.invalidated, ov.observationPlans.invalidated, t.plan.none)}
        {renderPlanGroup(t.plan.draft, ov.observationPlans.drafts, t.plan.none)}
      </div>
    </div>
  );

  const screenBadge =
    sm.state === 'failed' ? (
      <span className="shrink-0 rounded bg-gf-negative/15 px-1.5 py-0.5 text-[10px] text-gf-negative" title={sm.lastError || undefined}>
        {zh ? '评估失败' : 'Failed'}
      </span>
    ) : sm.state === 'delayed' ? (
      <span className="shrink-0 rounded bg-gf-warn/15 px-1.5 py-0.5 text-[10px] text-gf-warn">
        {zh ? '评估延迟' : 'Delayed'}
      </span>
    ) : sm.state === 'paused' ? (
      <span className="shrink-0 rounded bg-gf-surface2 px-1.5 py-0.5 text-[10px] text-gf-muted">
        {zh ? '已暂停' : 'Paused'}
      </span>
    ) : sm.state === 'no_subscription' || sm.count === 0 ? (
      <span className="shrink-0 rounded bg-gf-surface2 px-1.5 py-0.5 text-[10px] text-gf-muted">
        {zh ? '未创建订阅' : 'No subscription'}
      </span>
    ) : (
      <span className="shrink-0 rounded bg-gf-accent/15 px-1.5 py-0.5 text-[10px] text-gf-accent">
        {zh ? '监控中' : 'Monitoring'}
      </span>
    );

  const bodyHits = ov.screenHits.length ? (
    <ul className="divide-y divide-gf-border">
      {ov.screenHits.map((h) => {
        const snap = h.snapshot as Record<string, unknown> | null | undefined;
        const sym = (snap?.symbol as string) || h.symbol || '';
        const isExit = (snap?.condition as string) === 'exit';
        const ver = typeof snap?.ruleVersion === 'number' ? ` v${snap.ruleVersion}` : '';
        const hitTitle = zh ? h.title : isExit ? `${sym} exited` : `${sym} new hit`;
        const hitBody = zh
          ? h.body
          : isExit
            ? `${sym} left monitoring rule${ver}`
            : `${sym} entered monitoring rule${ver}`;
        return (
          <li key={h.id} className="py-2">
            <Link href={h.refUrl || '/alerts?type=screen'} className="block">
              <div className="text-sm font-medium text-gf-text">{hitTitle}</div>
              {h.body && <div className="mt-0.5 line-clamp-2 text-xs text-gf-muted">{hitBody}</div>}
              <div className="mt-1 text-[11px] text-gf-muted">{fmtDate(h.createdAt)}</div>
            </Link>
          </li>
        );
      })}
    </ul>
  ) : (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 rounded-lg bg-gf-surface2 px-3 py-2.5 text-xs text-gf-muted">
        <span>{screenEmptyMsg(ov.screenMonitor, zh)}</span>
        {(sm.state === 'no_subscription' || sm.count === 0) && (
          <Link href="/screener" className="shrink-0 text-gf-accent hover:underline">
            {zh ? '创建订阅 →' : 'Create →'}
          </Link>
        )}
      </div>
      {(sm.state === 'failed' || sm.state === 'delayed' || sm.state === 'monitoring' || sm.state === 'paused') && sm.count > 0 && (
        <div className="rounded-lg bg-gf-surface2/70 px-3 py-2 text-[10px] text-gf-muted">
          {lastEvalTxt}
          {sm.state === 'failed' && sm.lastError && (
            <div className="mt-1 break-all font-mono text-[9px] text-gf-negative/80" title={zh ? '点击复制诊断信息' : 'Click to copy diagnostics'}>
              {sm.lastError === 'rule_missing' ? (zh ? '规则缺失' : 'rule missing') : sm.lastError}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const bodyEvents = (
    <div className="space-y-2">
      {/* N05: 影响我的自选入口 */}
      <Link
        href="/research/impact"
        className="flex items-center justify-between rounded-lg border border-gf-border bg-gf-surface2/70 px-3 py-2 text-xs text-gf-muted transition-colors hover:border-gf-accent/40 hover:text-gf-text"
      >
        <span>{zh ? '影响我的自选' : 'Impact on my watchlist'}</span>
        <span className="text-gf-accent">{zh ? '未来 7 天 →' : 'Next 7 days →'}</span>
      </Link>
      {ov.events.length ? (
        <ul className="divide-y divide-gf-border">
          {ov.events.map((e) => (
            <li key={e.eventKey} className="flex items-center gap-2 py-2 text-sm">
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-gf-text">{e.title}</div>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gf-muted">
                  <span>{fmtDate(e.eventDate)}</span>
                  {e.status === 'sent' && <span className="rounded bg-gf-surface2 px-1 text-gf-muted">{zh ? '已提醒' : 'sent'}</span>}
                </div>
              </div>
              <span className="shrink-0 rounded bg-gf-surface2 px-1.5 py-0.5 text-[11px] text-gf-muted">
                {e.leadHours}h
              </span>
            </li>
          ))}
        </ul>
      ) : (
        emptyBlock(t.module.events.empty, '/research/events')
      )}
    </div>
  );

  const bodyAlerts = ov.alerts.length ? (
    <ul className="divide-y divide-gf-border">
      {ov.alerts.map((a) => (
        <li key={a.id} className="flex items-center gap-2 py-2 text-sm">
          <div className="min-w-0 flex-1">
            <Link href={`/coin/${a.symbol}`} className="font-medium text-gf-text hover:underline">
              {a.symbol}
            </Link>
            <div className="mt-0.5 text-[11px] text-gf-muted">
              {t.target} {fmtAlertCondition(a, zh)}
            </div>
          </div>
          <span className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] ${a.status === 'active' ? 'bg-gf-accent/15 text-gf-accent' : 'bg-gf-surface2 text-gf-muted'}`}>
            {a.status === 'active' ? (zh ? '监控中' : 'active') : zh ? '已触发' : 'triggered'}
          </span>
        </li>
      ))}
    </ul>
  ) : (
    emptyBlock(t.module.alerts.empty, '/alerts')
  );

  const bodySaved = (
    <div className="space-y-3">
      {/* D20: 已保存研究分类入口 — 历史任务 / 规则 / 布局 / 收藏 / 笔记 */}
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
        {([
          [zh ? '历史任务' : 'History', '/research/studies', ov.counts.rules],
          [zh ? '筛选规则' : 'Rules', '/screener', ov.savedResearch.rules.length],
          [zh ? '图表布局' : 'Layouts', '/chart', ov.counts.layouts],
          [zh ? '信号收藏' : 'Favorites', '/signals', ov.counts.favorites],
          [zh ? '研究笔记' : 'Notes', '/workspace', ov.counts.notes],
          [zh ? '条件订阅' : 'Subs', '/alerts?type=screen', ov.counts.subscriptions],
          [zh ? '观察计划' : 'Plans', '/workspace', ov.observationPlans.total],
        ] as const).map(([label, href, cnt]) => (
          <Link
            key={label}
            href={href}
            className="flex items-center justify-between rounded-lg border border-gf-border bg-gf-surface2/70 px-2.5 py-2 text-xs text-gf-muted transition-colors hover:border-gf-accent/40 hover:text-gf-text"
          >
            <span>{label}</span>
            <b className="text-gf-text">{cnt}</b>
          </Link>
        ))}
      </div>
      {(ov.savedResearch.rules.length > 0 || ov.savedResearch.layouts.length > 0) ? (
        <>
          {ov.savedResearch.rules.length > 0 && (
            <div>
              <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-gf-muted">{t.ruleCol}</div>
              <ul className="divide-y divide-gf-border rounded-lg bg-gf-surface2">
                {ov.savedResearch.rules.map((r) => (
                  <li key={r.id}>
                    <Link href={`/screener?rule=${r.id}`} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span className="truncate text-gf-text">{r.name}</span>
                      <span className="shrink-0 text-[11px] text-gf-muted">{fmtDate(r.updatedAt)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {ov.savedResearch.layouts.length > 0 && (
            <div>
              <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-gf-muted">{t.layoutCol}</div>
              <ul className="divide-y divide-gf-border rounded-lg bg-gf-surface2">
                {ov.savedResearch.layouts.map((l) => (
                  <li key={l.id}>
                    <Link href={`${buildCoinRoute(parseInstrumentId(l.instrumentId) ?? l.instrumentId, { tab: 'analysis' })}`} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span className="truncate text-gf-text">{l.name}</span>
                      <span className="shrink-0 text-[11px] text-gf-muted">{l.range}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        emptyBlock(t.module.saved_research.empty, '/screener')
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gf-muted">
        <span>{t.stat.subscriptions}: <b className="text-gf-text">{ov.savedResearch.subscriptions}</b></span>
        <span>{t.stat.favorites}: <b className="text-gf-text">{ov.savedResearch.favorites}</b></span>
        <span>{t.stat.notes}: <b className="text-gf-text">{ov.savedResearch.notes}</b></span>
      </div>
    </div>
  );

  const noteTypeLabel = (tt: string): string => {
    const map: Record<string, string> = {
      general: t.notes.general,
      signal: t.notes.signal,
      layout: t.notes.layout,
      rule: t.notes.rule,
      subscription: t.notes.subscription,
      study: t.notes.study,
      coin: t.notes.coin,
    };
    return map[tt] ?? tt;
  };

  const bodyNotes = (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <button onClick={() => openNote('create')} className="btn-outline !h-8 px-3 text-xs">
          + {t.newNote}
        </button>
        <Link href="/signals" className="text-[11px] text-gf-accent hover:underline">
          {t.fromSignal} →
        </Link>
      </div>
      {ov.notes.length ? (
        <ul className="divide-y divide-gf-border">
          {ov.notes.map((n) => (
            <li key={n.id} className="group py-2">
              <div className="line-clamp-3 whitespace-pre-wrap text-sm text-gf-text">{n.body}</div>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gf-muted">
                <span className="rounded bg-gf-surface2 px-1.5 py-0.5">
                  {noteTypeLabel(n.targetType)}
                </span>
                {n.targetSnapshot?.name ? (
                  n.targetType === 'signal' && n.available ? (
                    <Link href="/signals" className="max-w-[160px] truncate text-gf-accent hover:underline" title={zh ? '在工作台/信号页打开此信号' : 'Open this signal'}>
                      {String(n.targetSnapshot.name)}
                    </Link>
                  ) : (
                    <span className="max-w-[160px] truncate" title={String(n.targetSnapshot.name)}>
                      {String(n.targetSnapshot.name)}
                    </span>
                  )
                ) : n.targetId ? (
                  <span className="max-w-[160px] truncate font-mono text-[10px]">
                    {zh ? `目标 #${n.targetId}` : `Target #${n.targetId}`}
                  </span>
                ) : null}
                {n.targetType !== 'general' && !n.available && (
                  <span className="rounded bg-gf-warn/15 px-1.5 py-0.5 text-[10px] text-gf-warn">
                    {t.notes.unavailable}
                  </span>
                )}
                <span>·</span>
                <span>{fmtDate(n.updatedAt)}</span>
                <span className="ml-auto hidden gap-2 group-hover:flex">
                  <button onClick={() => openNote('edit', n)} className="text-gf-accent hover:underline">{t.edit}</button>
                  <button
                    onClick={() => {
                      if (confirm(t.confirmDel)) delNote.mutate(n.id, { onError: () => showToast(t.fail) });
                    }}
                    className="text-gf-negative hover:underline"
                  >
                    {t.del}
                  </button>
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="space-y-2">
          {emptyBlock(t.module.notes.empty)}
          <Link href="/signals" className="flex items-center justify-between rounded-lg bg-gf-surface2 px-3 py-2.5 text-xs text-gf-accent hover:bg-gf-surface2/70">
            <span>{t.fromSignal}</span>
            <span>→</span>
          </Link>
        </div>
      )}
    </div>
  );

  /* --------------------------- module render order --------------------------- */

  const leftModules = visibleModules.filter((m) =>
    ['watchlist_changes', 'screen_hits', 'saved_research', 'observation_plans'].includes(m.key),
  );
  const rightModules = visibleModules.filter((m) => ['events', 'alerts', 'notes'].includes(m.key));

  /* --------------------------- render --------------------------- */

  return (
    <div className="mx-auto max-w-[1200px] px-3 py-4 sm:px-4">
      <ResearchTabs />

      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold sm:text-2xl">{t.title}</h1>
          <p className="mt-0.5 text-xs text-gf-muted">{t.subtitle}</p>
          {/* D09: 数据更新时间 + 陈旧标记（轮询 60s，>120s 未更新视为陈旧） */}
          <div className={`mt-1 text-[10px] ${dataStale ? 'text-gf-warn' : 'text-gf-muted'}`}>
            {zh ? '数据更新于' : 'Updated'}: {fmtDate(ov.asOf)}
            {dataStale && <span className="ml-1.5">（{zh ? '数据陈旧' : 'stale'}）</span>}
          </div>
        </div>
        <button onClick={() => setSettingsOpen(true)} className="btn-outline !h-9 text-xs">
          ⚙ {t.customize}
        </button>
      </div>

      {/* D20: 顶部统计做紧凑快捷入口（chip 行），不把重要内容推下 */}
      <div className="mb-4 flex flex-wrap gap-1.5">
        {([
          ['watchlist', ov.counts.watchlist, '/watchlist'],
          ['rules', ov.counts.rules, '/screener'],
          ['layouts', ov.counts.layouts, '/chart'],
          ['subscriptions', ov.counts.subscriptions, '/alerts?type=screen'],
          ['favorites', ov.counts.favorites, '/signals'],
          ['notes', ov.counts.notes, '/workspace'],
        ] as const).map(([k, v, href]) => (
          <Link
            key={k}
            href={href}
            className="flex items-baseline gap-1.5 rounded-lg border border-gf-border bg-gf-surface2/70 px-2.5 py-1.5 text-xs text-gf-muted transition-colors hover:border-gf-accent/40 hover:text-gf-text"
          >
            <b className="text-sm font-bold text-gf-text">{v}</b>
            {t.stat[k]}
          </Link>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* left 2/3 */}
        <div className="space-y-4 lg:col-span-2">
          {leftModules.map((m) => {
            if (m.key === 'watchlist_changes') return <div key={m.key}>{renderModuleCard('watchlist_changes', bodyWatchlist, '/watchlist')}</div>;
            if (m.key === 'screen_hits') return <div key={m.key}>{renderModuleCard('screen_hits', bodyHits, '/alerts?type=screen', screenBadge)}</div>;
            if (m.key === 'observation_plans') return <div key={m.key}>{renderModuleCard('observation_plans', bodyObservationPlans)}</div>;
            return <div key={m.key}>{renderModuleCard('saved_research', bodySaved, '/screener')}</div>;
          })}
        </div>
        {/* right 1/3 */}
        <div className="space-y-4">
          {rightModules.map((m) => {
            if (m.key === 'events') return <div key={m.key}>{renderModuleCard('events', bodyEvents, '/research/events')}</div>;
            if (m.key === 'alerts') return <div key={m.key}>{renderModuleCard('alerts', bodyAlerts, '/alerts')}</div>;
            return <div key={m.key}>{renderModuleCard('notes', bodyNotes)}</div>;
          })}
        </div>
      </div>

      {/* settings dialog */}
      {settingsOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center" onClick={() => setSettingsOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border border-gf-border bg-gf-surface p-4" onClick={(e) => e.stopPropagation()}>
            <div className="mb-1 text-base font-semibold text-gf-text">{t.setting.title}</div>
            <p className="mb-3 text-xs text-gf-muted">{t.setting.hint}</p>
            <ul className="space-y-2">
              {modules.map((m, i) => (
                <li key={m.key} className="flex items-center gap-2 rounded-lg bg-gf-surface2 px-3 py-2.5">
                  <button
                    onClick={() => {
                      const next = [...modules];
                      if (i === 0) return;
                      const prev = next[i - 1];
                      next[i - 1] = { ...next[i], order: prev.order };
                      next[i] = { ...prev, order: m.order };
                      setModules(next);
                    }}
                    className="flex h-7 w-7 items-center justify-center rounded-md border border-gf-border text-gf-muted hover:text-gf-text disabled:opacity-30"
                    disabled={i === 0}
                    aria-label="move up"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => {
                      const next = [...modules];
                      if (i >= next.length - 1) return;
                      const nxt = next[i + 1];
                      next[i + 1] = { ...next[i], order: nxt.order };
                      next[i] = { ...nxt, order: m.order };
                      setModules(next);
                    }}
                    className="flex h-7 w-7 items-center justify-center rounded-md border border-gf-border text-gf-muted hover:text-gf-text disabled:opacity-30"
                    disabled={i >= modules.length - 1}
                    aria-label="move down"
                  >
                    ↓
                  </button>
                  <span className="flex-1 text-sm text-gf-text">{t.module[m.key as ModuleKey].name}</span>
                  <button
                    onClick={() => {
                      setModules(modules.map((x) => (x.key === m.key ? { ...x, visible: !x.visible } : x)));
                    }}
                    className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
                      m.visible
                        ? 'border-gf-accent/40 bg-gf-accent/15 text-gf-accent'
                        : 'border-gf-border text-gf-muted'
                    }`}
                  >
                    {m.visible ? t.setting.visible : zh ? '隐藏' : 'Hidden'}
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex items-center justify-between gap-2">
              <button
                onClick={() => {
                  setModules(MODULE_KEYS.map((key, order) => ({ key, visible: true, order })));
                  saveLayout.mutate(
                    MODULE_KEYS.map((key, order) => ({ key, visible: true, order })),
                    { onSuccess: () => showToast(t.restoreDefault) },
                  );
                }}
                className="btn-outline !h-9 text-xs"
              >
                {t.restoreDefault}
              </button>
              <div className="flex gap-2">
                <button onClick={() => setSettingsOpen(false)} className="btn-outline !h-9 text-xs">
                  {t.cancel}
                </button>
                <button
                  onClick={() =>
                    saveLayout.mutate(modules, { onError: () => showToast(t.fail), onSuccess: () => showToast(t.saved) })
                  }
                  className="btn-primary !h-9 text-xs"
                  disabled={saveLayout.isPending}
                >
                  {saveLayout.isPending ? t.loading : t.save}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* note dialog */}
      {noteModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center" onClick={() => setNoteModal(null)}>
          <div className="w-full max-w-lg rounded-2xl border border-gf-border bg-gf-surface p-4" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 text-base font-semibold text-gf-text">
              {noteModal.mode === 'edit' ? t.edit : t.newNote}
            </div>
            <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs text-gf-muted">{t.notes.targetType}</span>
                <select
                  value={noteTarget}
                  onChange={(e) => {
                    setNoteTarget(e.target.value as (typeof NOTE_TARGETS)[number]);
                    setNotePick(null);
                    setNoteQ('');
                  }}
                  className="input h-10 w-full"
                >
                  {NOTE_TARGETS.map((tt) => (
                    <option key={tt} value={tt}>
                      {tt === 'general' ? t.notes.general : tt === 'signal' ? t.notes.signal : tt === 'layout' ? t.notes.layout : tt === 'rule' ? t.notes.rule : tt === 'subscription' ? t.notes.subscription : tt === 'study' ? t.notes.study : t.notes.coin}
                    </option>
                  ))}
                </select>
              </label>
              {noteTarget !== 'general' ? (
                <label className="block">
                  <span className="mb-1 block text-xs text-gf-muted">{t.notes.targetSearch}</span>
                  {notePick ? (
                    <div className="flex h-10 items-center gap-2 rounded-lg border border-gf-accent/40 bg-gf-accent/10 px-2.5 text-xs text-gf-text">
                      <span className="truncate flex-1">{notePick.name}</span>
                      <span className="shrink-0 rounded bg-gf-surface2 px-1.5 py-0.5 text-[10px] text-gf-muted">{t.notes.targetPicked}</span>
                      <button
                        onClick={() => {
                          setNotePick(null);
                          setNoteQ('');
                        }}
                        className="shrink-0 text-gf-muted hover:text-gf-text"
                        aria-label="clear"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        value={noteQ}
                        onChange={(e) => setNoteQ(e.target.value)}
                        placeholder={t.notes.targetSearch}
                        className="input h-10 w-full"
                      />
                      {noteQ.trim().length >= 1 && (
                        <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-44 overflow-y-auto rounded-lg border border-gf-border bg-gf-surface shadow-lg">
                          {targetQ.isLoading ? (
                            <div className="px-3 py-2 text-xs text-gf-muted">{t.loading}</div>
                          ) : targetQ.data?.length ? (
                            targetQ.data.map((opt) => (
                              <button
                                key={opt.value}
                                onClick={() => {
                                  setNotePick({ value: opt.value, name: opt.name });
                                  setNoteQ('');
                                }}
                                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs text-gf-text hover:bg-gf-surface2"
                              >
                                <span className="truncate">{opt.name}</span>
                                {opt.sub && <span className="shrink-0 text-[10px] text-gf-muted">{opt.sub}</span>}
                              </button>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-xs text-gf-muted">{t.notes.noResult}</div>
                          )}
                        </div>
                      )}
                      {noteQ.trim().length === 0 && <div className="mt-1 text-[10px] text-gf-muted">{t.notes.searchHint}</div>}
                    </div>
                  )}
                </label>
              ) : (
                <div className="flex items-end pb-1 text-[11px] text-gf-muted">{t.notes.general}</div>
              )}
            </div>
            <textarea
              value={noteBody}
              onChange={(e) => setNoteBody(e.target.value.slice(0, 5000))}
              placeholder={t.notes.placeholder}
              rows={5}
              className="input w-full resize-none"
            />
            <div className="mt-1 text-right text-[11px] text-gf-muted">{t.notes.count(noteBody.length)}</div>
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setNoteModal(null)} className="btn-outline !h-9 text-xs">
                {t.cancel}
              </button>
              <button
                onClick={submitNote}
                disabled={saveNote.isPending || !noteBody.trim()}
                className="btn-primary !h-9 text-xs disabled:opacity-50"
              >
                {saveNote.isPending ? t.loading : t.save}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* N03: plan dialog */}
      {planModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center" onClick={() => setPlanModal(null)}>
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-gf-border bg-gf-surface p-4" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 text-base font-semibold text-gf-text">
              {planModal.mode === 'edit' ? `${t.edit} · ${t.plan.version}${planModal.plan.version}` : t.plan.new}
            </div>
            <div className="space-y-3">
              <label className="block">
                <span className="mb-1 block text-xs text-gf-muted">{t.plan.title}</span>
                <input value={planTitle} onChange={(e) => setPlanTitle(e.target.value)} placeholder={t.plan.titlePh} className="input h-10 w-full" maxLength={200} />
              </label>

              <label className="block">
                <span className="mb-1 block text-xs text-gf-muted">{t.plan.instruments}</span>
                <div className="space-y-2">
                  {planInsts.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {planInsts.map((ins) => (
                        <span key={ins.symbol} className="flex items-center gap-1 rounded-md border border-gf-accent/40 bg-gf-accent/10 px-2 py-1 text-xs text-gf-text">
                          {ins.symbol}
                          <button
                            onClick={() => setPlanInsts(planInsts.filter((x) => x.symbol !== ins.symbol))}
                            className="text-gf-muted hover:text-gf-text"
                            aria-label="remove"
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <input
                      value={planInstQ}
                      onChange={(e) => setPlanInstQ(e.target.value)}
                      placeholder={t.plan.instrumentsPh}
                      className="input h-10 w-full"
                    />
                    {planInstQ.trim().length >= 1 && (
                      <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-44 overflow-y-auto rounded-lg border border-gf-border bg-gf-surface shadow-lg">
                        {planCoinQ.isLoading ? (
                          <div className="px-3 py-2 text-xs text-gf-muted">{t.loading}</div>
                        ) : planCoinQ.data?.length ? (
                          planCoinQ.data.map((opt) => (
                            <button
                              key={opt.value}
                              onClick={() => {
                                if (!planInsts.some((x) => x.symbol === opt.value)) {
                                  setPlanInsts([...planInsts, { symbol: opt.value, product: opt.sub?.includes('现货') || opt.sub?.includes('Spot') ? 'spot' : 'perp' }]);
                                }
                                setPlanInstQ('');
                              }}
                              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs text-gf-text hover:bg-gf-surface2"
                            >
                              <span className="truncate">{opt.name}</span>
                              {opt.sub && <span className="shrink-0 text-[10px] text-gf-muted">{opt.sub}</span>}
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-xs text-gf-muted">{t.notes.noResult}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </label>

              <label className="block">
                <span className="mb-1 block text-xs text-gf-muted">{t.plan.hypothesis}</span>
                <textarea value={planHypo} onChange={(e) => setPlanHypo(e.target.value.slice(0, 10000))} placeholder={t.plan.hypothesisPh} rows={3} className="input w-full resize-none" />
              </label>

              <label className="block">
                <span className="mb-1 block text-xs text-gf-muted">{t.plan.evidence}</span>
                <textarea value={planEvid} onChange={(e) => setPlanEvid(e.target.value)} placeholder={t.plan.evidencePh} rows={2} className="input w-full resize-none" />
              </label>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs text-gf-muted">{t.plan.entryRule}</span>
                  <select value={planEntry} onChange={(e) => setPlanEntry(e.target.value)} className="input h-10 w-full">
                    <option value="">{t.plan.noRule}</option>
                    {(subsQ.data ?? []).map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.screenName ?? s.id.slice(0, 8)} ({s.state ?? ''})
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs text-gf-muted">{t.plan.invalidationRule}</span>
                  <select value={planInvalid} onChange={(e) => setPlanInvalid(e.target.value)} className="input h-10 w-full">
                    <option value="">{t.plan.noRule}</option>
                    {(subsQ.data ?? []).map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.screenName ?? s.id.slice(0, 8)} ({s.state ?? ''})
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs text-gf-muted">{t.plan.reviewAt}</span>
                  <input type="date" value={planReview} onChange={(e) => setPlanReview(e.target.value)} className="input h-10 w-full" />
                  <span className="mt-1 block text-[10px] text-gf-muted">{t.plan.reviewHint}</span>
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs text-gf-muted">{t.plan.notes}</span>
                  <input value={planNotes} onChange={(e) => setPlanNotes(e.target.value.slice(0, 5000))} placeholder={t.plan.notesPh} className="input h-10 w-full" />
                </label>
              </div>

              {!planEntry || !planReview ? (
                <div className="rounded-lg bg-gf-warn/10 px-3 py-2 text-[11px] text-gf-warn">{t.plan.incomplete}</div>
              ) : null}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setPlanModal(null)} className="btn-outline !h-9 text-xs">
                {t.cancel}
              </button>
              <button
                onClick={submitPlan}
                disabled={savePlan.isPending || !planTitle.trim() || !planHypo.trim() || !planInsts.length}
                className="btn-primary !h-9 text-xs disabled:opacity-50"
              >
                {savePlan.isPending ? t.loading : t.plan.save}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* N03: rule version confirm dialog */}
      {ruleChangePending && (
        <div className="fixed inset-0 z-[55] flex items-end justify-center bg-black/40 p-4 sm:items-center" onClick={() => setRuleChangePending(null)}>
          <div className="w-full max-w-md rounded-2xl border border-gf-border bg-gf-surface p-4" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 text-base font-semibold text-gf-text">{t.plan.ruleChangeConfirm}</div>
            <div className="mb-4 rounded-lg bg-gf-surface2 px-3 py-2 text-xs text-gf-muted">
              {t.plan.version} {planModal?.mode === 'edit' ? (planModal.plan.version + 1) : 2} → {t.plan.version} {planModal?.mode === 'edit' ? (planModal.plan.version + 1) : 2}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setRuleChangePending(null)} className="btn-outline !h-9 text-xs">
                {t.cancel}
              </button>
              <button
                onClick={() => {
                  setPlanConfirmVersion(true);
                  const payload = ruleChangePending;
                  setRuleChangePending(null);
                  savePlan.mutate({ ...payload, confirmVersion: true }, { onError: () => showToast(t.fail) });
                }}
                className="btn-primary !h-9 text-xs"
              >
                {t.save}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* toast */}
      {toast && (
        <div className="fixed bottom-20 left-1/2 z-[60] -translate-x-1/2 rounded-lg bg-gf-text px-4 py-2 text-xs font-medium text-gf-surface shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
